import type {
  NotificationDevice,
  PushSubscriptionUpsert,
} from '@email/shared';
import { registerPushSubscription } from '../api/notifications.js';
import { getPwaServiceWorkerRegistration } from './lifecycle.js';

export interface PushBrowserState {
  supported: boolean;
  secure: boolean;
  permission: NotificationPermission | 'unsupported';
  endpointHash: string | null;
}

export function pushBrowserSupport(): Omit<
  PushBrowserState,
  'endpointHash'
> {
  const supported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;
  return {
    supported,
    secure: typeof window !== 'undefined' && window.isSecureContext,
    permission: supported ? Notification.permission : 'unsupported',
  };
}

export async function currentPushBrowserState(): Promise<PushBrowserState> {
  const support = pushBrowserSupport();
  if (!support.supported) return { ...support, endpointHash: null };
  const registration = await getPwaServiceWorkerRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return {
    ...support,
    permission: Notification.permission,
    endpointHash: subscription
      ? await hashPushEndpoint(subscription.endpoint)
      : null,
  };
}

export async function enablePushOnThisDevice(
  publicKey: string,
  deviceName: string,
): Promise<NotificationDevice> {
  const support = pushBrowserSupport();
  if (!support.supported) {
    throw new Error('This browser does not support Web Push notifications.');
  }
  if (!support.secure) {
    throw new Error('Notifications require Wintermute to be opened over HTTPS.');
  }
  const permission =
    Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifications are blocked in this browser. Allow them in the site permissions first.'
        : 'Notification permission was not granted.',
    );
  }
  const registration = await getPwaServiceWorkerRegistration();
  if (!registration) {
    throw new Error(
      'The Wintermute app worker is not ready. Reload the deployed app and try again.',
    );
  }
  const applicationServerKey = decodeApplicationServerKey(publicKey);
  let subscription = await registration.pushManager.getSubscription();
  let replaceEndpoint: string | undefined;
  if (
    subscription &&
    !sameApplicationServerKey(
      subscription.options.applicationServerKey,
      applicationServerKey,
    )
  ) {
    replaceEndpoint = subscription.endpoint;
    await subscription.unsubscribe();
    subscription = null;
  }
  subscription ??= await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
  const value = serializePushSubscription(
    subscription,
    deviceName,
    replaceEndpoint,
  );
  const result = await registerPushSubscription(value);
  return result.device;
}

export async function unsubscribeCurrentPushDevice(): Promise<void> {
  const registration = await getPwaServiceWorkerRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  await subscription?.unsubscribe();
}

export async function hashPushEndpoint(endpoint: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(endpoint),
  );
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export function suggestedDeviceName(): string {
  const userAgent = navigator.userAgent;
  if (/iPhone/i.test(userAgent)) return 'iPhone';
  if (/iPad/i.test(userAgent)) return 'iPad';
  if (/Android/i.test(userAgent)) return 'Android phone';
  if (/Windows/i.test(userAgent)) return 'Windows browser';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'Mac browser';
  if (/Linux/i.test(userAgent)) return 'Linux browser';
  return 'This device';
}

export async function setWintermuteBadge(count: number | null): Promise<void> {
  const navigatorWithBadge = navigator as Navigator & {
    setAppBadge?: (contents?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
  try {
    if (count === null || count <= 0) {
      await navigatorWithBadge.clearAppBadge?.();
    } else {
      await navigatorWithBadge.setAppBadge?.(Math.min(count, 9999));
    }
  } catch {
    // Badges are an optional enhancement and vary by browser/OS.
  }
}

function serializePushSubscription(
  subscription: PushSubscription,
  deviceName: string,
  replaceEndpoint?: string,
): PushSubscriptionUpsert {
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
    throw new Error('The browser returned an incomplete push subscription.');
  }
  return {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: {
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    },
    deviceName: deviceName.trim() || suggestedDeviceName(),
    ...(replaceEndpoint ? { replaceEndpoint } : {}),
  };
}

function decodeApplicationServerKey(value: string): ArrayBuffer {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(
    value.length + ((4 - (value.length % 4)) % 4),
    '=',
  );
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return bytes.buffer;
}

function sameApplicationServerKey(
  existing: ArrayBuffer | null,
  expected: ArrayBuffer,
): boolean {
  if (!existing) return false;
  const left = new Uint8Array(existing);
  const right = new Uint8Array(expected);
  return (
    left.length === right.length && left.every((byte, index) => byte === right[index])
  );
}
